/**
 * Song metadata display + print — shared by LyricsSheet (phone/iPad) and
 * RightColumn (desktop), the two lyrics presentations that are never both
 * reachable at once (width-gated), but both need the identical meta-line
 * composer and print output.
 */

/** "Simple Gifts · Shaker hymn · Joseph Brackett / Martin Charnin · arr. Tim
 *  Weil · 1848" — every field is optional across the three metadata source
 *  APIs, so this stays defensive rather than assuming a fixed shape. */
export const buildSongMeta = (song) => {
	const m = song?.metadata;
	if (!m) return '';
	const parts = [];
	if (m.isMusical && m.musicalName) parts.push(m.musicalName);
	// De-duplicated because one person is very often BOTH, and the two arrays
	// are populated independently. Observed live 2026-08-05 searching "Crazy
	// for You": the Original London Cast release credits "George & Ira
	// Gershwin" as composer AND lyricist, so the line read "George & Ira
	// Gershwin / George & Ira Gershwin" — which also consumed the entire
	// 2-name budget on one name. Exact-string only, deliberately: these come
	// from the same source field, and normalising further risks merging two
	// genuinely different people who happen to look alike.
	const credits = Array.from(
		new Set([...(m.composers || []), ...(m.lyricists || [])].filter(Boolean))
	);
	if (credits.length) parts.push(credits.slice(0, 2).join(' / '));

	// Arrangers get their OWN part rather than joining `credits`, for two
	// reasons. (1) `credits` is capped at 2, so merging would let an arranger
	// push a composer off the line — and the composer identifies the song
	// while the arranger identifies this treatment of it. (2) An unlabelled
	// name in a credit list reads as a writer; "arr." is the conventional
	// notation and costs four characters to remove the ambiguity.
	//
	// Deliberately "arr." and not "Arranger:" or "Orchestrator:": Discogs
	// spells the underlying credit many ways (`Arranged By [Strings]`,
	// `Producer, Arranged By`, `Orchestrated By`) and the role matcher takes
	// all of them, so asserting a specific job title would sometimes be wrong.
	// "arr." is true of every one of them.
	const arrangers = (m.arrangers || []).filter(Boolean);
	if (arrangers.length) parts.push(`arr. ${arrangers.slice(0, 2).join(' / ')}`);

	const year = m.releaseYear || m.yearWritten;
	if (year) parts.push(String(year));
	return parts.join(' · ');
};

export const escapeHtml = (s = '') =>
	s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** A clean print window: title + meta + lyrics, nothing else (design handoff
 *  §5). The old SongNotes.js popup did much more (an editable-before-print
 *  flow, a reminder banner) — this is a smaller, from-scratch implementation
 *  matching the simpler target the redesign actually asks for, not a port. */
export const printLyrics = (title, meta, text) => {
	const win = window.open('', '_blank', 'width=700,height=900');
	if (!win) return;
	win.document.write(`<!doctype html><html><head><title>${escapeHtml(title)}</title><style>
		body{font-family:Georgia,'Times New Roman',serif;color:#222;padding:36px;max-width:640px;margin:0 auto;}
		h1{font-size:20px;margin:0 0 4px;}
		.meta{font-size:12px;color:#666;margin:0 0 22px;}
		.lyrics{font-size:14px;line-height:1.7;white-space:pre-wrap;}
	</style></head><body>
		<h1>${escapeHtml(title)}</h1>
		${meta ? `<p class="meta">${escapeHtml(meta)}</p>` : ''}
		<div class="lyrics">${escapeHtml(text)}</div>
	</body></html>`);
	win.document.close();
	win.focus();
	win.print();
};
